import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Box, Text } from '@ltvco/refresh-lib/theme';
import {
  ListedVehicles,
  useUserVehicles,
  buildMarkSoldFormData,
  type UserVehicle,
} from '@ltvco/refresh-lib/v2';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';

function openListingInNewTab(vehicle: UserVehicle): void {
  const prefix = (import.meta.env.VITE_PATH_PREFIX as string | undefined) ?? '';
  const id = vehicle.inventoryId ?? vehicle.id ?? '';
  if (!id) {
    return;
  }
  window.open(
    `${prefix}/marketplace/vehicle/${vehicle.vin ?? id}`,
    '_blank',
    'noopener',
  );
}

/**
 * Authenticated listed-vehicles route — the user's own private-seller listings.
 * Ported from the Ember `listed-vehicles` route; mounts the lib `ListedVehicles`
 * view and wires create/edit navigation + the delete and mark-as-sold mutations
 * from `useUserVehicles`.
 */
export function MarketplaceListedVehicles() {
  const navigate = useNavigate();
  const { vehiclesQuery, updateVehicleMutation, deleteVehicleMutation } =
    useUserVehicles();
  const { trackEvent } = useContext(AppConfig);

  const vehicles = vehiclesQuery.data?.vehicles ?? [];

  const handleAddListing = (): void => {
    navigate('/marketplace/list-your-car');
  };

  const handleEditListing = (vehicle: UserVehicle): void => {
    const id = vehicle.inventoryId ?? vehicle.id;
    if (id) {
      navigate(`/marketplace/list-your-car/${id}`);
    }
  };

  const handleDeleteVehicle = (inventoryId: string, wasSold: boolean): void => {
    trackEvent('List your car', 'Delete listing', wasSold ? 'Sold' : 'Unsold');
    deleteVehicleMutation.mutate(inventoryId, {
      onSuccess: () => vehiclesQuery.refetch(),
    });
  };

  const handleMarkSold = (
    inventoryId: string,
    soldOverAsking: boolean,
  ): void => {
    trackEvent(
      'List your car',
      'Mark listing as sold',
      soldOverAsking ? 'Over' : 'Under',
    );
    updateVehicleMutation.mutate(
      { inventoryId, body: buildMarkSoldFormData(soldOverAsking) },
      { onSuccess: () => vehiclesQuery.refetch() },
    );
  };

  return (
    <Box maxWidth={1290} mx="auto" px={2} py={3}>
      <Text variant="h4" fontWeight={600} mb={3}>
        Your Listings
      </Text>
      <ListedVehicles
        vehicles={vehicles}
        onAddListing={handleAddListing}
        onEditListing={handleEditListing}
        onDeleteVehicle={handleDeleteVehicle}
        onMarkSold={handleMarkSold}
        onOpenListing={openListingInNewTab}
      />
    </Box>
  );
}
